<script setup>
  import { ref, inject, onMounted, computed, watch } from 'vue'
  import { useRouter, useRoute } from 'vue-router'
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import DataTable from '@/components/DataTable.vue'
  import EditDialog from '@/components/EditDialog.vue'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useCompanyStore } from '@/stores/companyStore'
  import { useStaffStore } from '@/stores/staffStore'
  import { useTodoStore } from '@/stores/todoStore'
  import { formatDate } from '@/components/logics/dateUtil'

  const router = useRouter()
  const route = useRoute()

  const dialogStore = useDialogStore()
  const todoStore = useTodoStore()
  const companyStore = useCompanyStore()
  const staffStore = useStaffStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: '/dashboard' }, { name: '案件進捗一覧' }]

  /////////////////////////////////////////////////////

  // data
  const searchText = ref('')
  const searchKey = ref('status')
  const searchSelect = ref('incomplete')
  const searchList = ref(['completed', 'incomplete'])
  const sortKey = ref('')
  const sortDesc = ref(false)
  const length = ref('')

  // 編集用データ
  const showPopupEdit = ref(false)
  const isEdit = ref(false)
  const note = ref('')
  const label = ref('')

  // 種別選択用
  const previousTodoMasterId = ref(null)

  // computed /////////////////////////////////////////
  // 削除ボタンの有効無効
  const isShowDeleteButton = computed(() => {
    return isEdit.value
  })

  // TodoMasterリスト
  const todoMasterOptions = computed(() => {
    return todoStore.todoMasterOptions
  })

  // ステータスオプション
  const statusOptions = computed(() => {
    return todoStore.getStatusOptions()
  })

  // Todoの選択肢
  const todoOptions = computed(() => {
    return todoStore.todoOptions || []
  })

  // 会社の選択肢
  const companyOptions = computed(() => {
    return todoStore.companyNameOptions || []
  })

  // 事業場の選択肢
  const branchOptions = computed(() => {
    return todoStore.branchNameOptions || []
  })

  // スタッフの選択肢
  const staffOptions = computed(() => {
    return todoStore.staffNameOptions || []
  })

  // フラット化したアイテムリストを取得
  const flatItems = computed(() => {
    return todoStore.flatItems || []
  })

  // フロー全体の完了状態をチェック
  const isFlowCompleted = computed(() => {
    // ストアのメソッドを利用
    return todoStore.isFlowComplete()
  })

  // 会社・事業場・従業員セレクト状態
  const companySelectState = computed(() => todoStore.getCompanySelectState(isEdit.value))
  const branchSelectState = computed(() => todoStore.getBranchSelectState(isEdit.value))
  const staffSelectState = computed(() => todoStore.getStaffSelectState(isEdit.value))

  // UI処理 ///////////////////////////////////////
  onMounted(async () => {
    // ステータスで絞り込み
    if (route.query.searchText) {
      searchSelect.value = 'incomplete'
      searchText.value = route.query.searchText
    }

    // テーブル定義の初期化
    todoStore.initTableList()

    // Todo一覧を取得
    await todoStore.getTodos()

    // TodoMasterを取得
    await todoStore.getTodoMasters()

    // 会社情報を取得
    await todoStore.getCompanyNames()

    // クエリのactionがnewの場合、新規登録ダイアログを開く
    if (route.query.action === 'new') {
      openEditDialog()
    }
  })

  // Todo編集ダイアログを開く
  const openEditDialog = async item => {
    isEdit.value = !!item
    await todoStore.setFormData(item?.id)
    showPopupEdit.value = true
  }

  // Todoマスタ一覧に遷移
  const gotoTodoMaster = () => {
    router.push({ name: 'todomaster' })
  }

  // Todo保存
  const onSave = async () => {
    const message = await todoStore.saveTodo()
    if (message) {
      showPopupEdit.value = false
      await dialogStore.showMessageDialog('確認', message)
    }
  }

  // コピー
  const onCopy = async () => {
    if (todoStore.formData) {
      // IDをnullにして新規として保存
      todoStore.onCopy()
      isEdit.value = false
    }
  }

  // Todo削除
  const onDelete = async () => {
    if (await dialogStore.showConfirmDialog('確認', 'この案件を削除しますか？')) {
      const message = await todoStore.deleteTodo()
      if (message) {
        showPopupEdit.value = false
        await dialogStore.showMessageDialog('確認', message)
      }
    }
  }

  // 会社が変更されたときの処理
  const onCompanyChange = async () => {
    // 削除フラグをリセット
    todoStore.isDeletedCompany = false
    todoStore.isDeletedBranch = false
    todoStore.isDeletedStaff = false

    await todoStore.onCompanyChange()
  }

  // 事業場が変更されたときの処理
  const onBranchChange = async () => {
    // 削除フラグをリセット
    todoStore.isDeletedBranch = false
    todoStore.isDeletedStaff = false

    await todoStore.onBranchChange()
  }

  // 従業員変更時の処理
  const onStaffChange = () => {
    todoStore.isDeletedStaff = false
  }

  // TodoMaster変更時の処理
  const onTodoMasterChange = async event => {
    const newTodoMasterId = event.target.value

    if (!newTodoMasterId) return

    // 編集時、または新規登録時でフローに変更がある場合は確認ダイアログを表示
    if (isEdit.value || todoStore.hasFlowChanges()) {
      const confirmed = await dialogStore.showConfirmDialog('確認', '種別を変更すると選択したデータがすべて失われます。変更しますか？')

      if (!confirmed) {
        // キャンセルの場合は元の値に戻す
        todoStore.formData.todoMasterId = previousTodoMasterId.value
        return
      }
    }

    // TodoMasterの変更をストアに通知
    await todoStore.onTodoMasterChange(newTodoMasterId)
  }

  // 選択肢を選択（確認ダイアログを表示してから）
  const selectOption = async (parentItem, option) => {
    if (!parentItem || !parentItem.selection) return

    // 選択肢変更前に確認
    if (todoStore.confirmSelectionChange(parentItem, option)) {
      // 変更に問題がない場合はそのまま選択
      todoStore.selectOption(parentItem, option)
    } else {
      // 確認が必要な場合
      if (await dialogStore.showConfirmDialog('確認', '選択肢を変更すると、これ以降のチェック状態と選択状態が失われます。よろしいですか？')) {
        todoStore.selectOption(parentItem, option)
      }
    }
  }

  // 備考表示
  const showNote = (event, item) => {
    if (!item) return

    event.preventDefault()

    // ダイアログ表示用のデータを設定
    note.value = item.tip || ''
    label.value = item.label || ''

    // 備考ダイアログ表示
    dialogStore.showMessageDialog(label.value, note.value)
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <h2>案件進捗一覧</h2>
      <button type="button" class="new" @click="openEditDialog()">新規登録</button>
      <button type="button" class="todo-master" @click="gotoTodoMaster()">案件マスタ一覧</button>
      <div class="filters">
        <div class="select">
          ステータス:
          <select v-model="searchSelect">
            <option value="">すべて</option>
            <option value="completed">完了</option>
            <option value="incomplete">未完了</option>
          </select>
        </div>
        <div class="search-box">
          <span class="material-symbols-outlined">search</span>
          <input type="search" v-model="searchText" placeholder="検索" />
        </div>
        <div class="search-result">
          {{ length }}
        </div>
      </div>
    </div>

    <div class="main-body">
      <DataTable :table-list="todoStore.tableList" :data-list="todoStore.todos" :search-text="searchText" :search-key="searchKey" :search-select="searchSelect" :search-list="searchList" :sort-key="sortKey" :sort-desc="sortDesc" @select="openEditDialog" @length="length = $event"></DataTable>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />

  <EditDialog v-model:visible="showPopupEdit" :show-delete-button="isEdit" :show-copy-button="isEdit" large :save-button-text="isEdit ? '保存' : '登録'" @save="onSave" @copy="onCopy" @delete="onDelete">
    <template v-slot:title>{{ isEdit ? '編集' : '新規登録' }}</template>

    <template v-slot:select>
      <div class="select">
        <select v-model="todoStore.formData.todoMasterId" @focus="previousTodoMasterId = todoStore.formData.todoMasterId" @change="onTodoMasterChange">
          <option v-if="todoStore.formData.todoMasterId === null" value="null" disabled>
            {{ isEdit ? '削除された種別' : '種別選択' }}
          </option>
          <option v-else value="null" disabled>種別選択</option>
          <option v-for="option in todoOptions" :key="option.value" :value="option.value">
            {{ option.label }}
          </option>
        </select>
      </div>
    </template>

    <template v-slot:content>
      <div class="dialog-edit">
        <div class="dialog-grid">
          <!-- 基本情報セクション -->
          <dl class="sticky">
            <dt>会社</dt>
            <dd>
              <div class="select">
                <select v-model="todoStore.formData.companyId" @change="onCompanyChange">
                  <!-- 削除された会社の場合 -->
                  <option v-if="companySelectState === 'deleted'" :value="todoStore.formData.companyId" disabled>削除された会社</option>
                  <!-- 会社選択を促す場合 -->
                  <option v-else-if="companySelectState === 'select'" value="null" disabled>会社を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>会社を選択</option>
                  <option v-for="company in companyOptions" :key="company.value" :value="company.value">
                    {{ company.label }}
                  </option>
                </select>
              </div>
            </dd>

            <dt>事業場</dt>
            <dd>
              <div class="select">
                <select v-model="todoStore.formData.branchId" @change="onBranchChange" :disabled="!todoStore.formData.companyId">
                  <!-- 削除された事業場の場合 -->
                  <option v-if="branchSelectState === 'deleted'" :value="todoStore.formData.branchId" disabled>削除された事業場</option>
                  <!-- 事業場選択を促す場合 -->
                  <option v-else-if="branchSelectState === 'select'" value="null" disabled>事業場を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>事業場を選択</option>
                  <option v-for="branch in branchOptions" :key="branch.value" :value="branch.value">
                    {{ branch.label }}
                  </option>
                </select>
              </div>
            </dd>

            <dt>従業員</dt>
            <dd>
              <div class="select">
                <select v-model="todoStore.formData.staffId" @change="onStaffChange" :disabled="!todoStore.formData.branchId">
                  <!-- 削除された従業員の場合 -->
                  <option v-if="staffSelectState === 'deleted'" :value="todoStore.formData.staffId" disabled>削除された従業員</option>
                  <!-- 従業員選択を促す場合 -->
                  <option v-else-if="staffSelectState === 'select'" value="null" disabled>従業員を選択</option>
                  <!-- 通常の無効状態の場合 -->
                  <option v-else value="null" disabled>従業員を選択</option>
                  <option v-for="option in staffOptions" :key="option.value" :value="option.value">
                    {{ option.label }}
                  </option>
                </select>
              </div>
            </dd>
            <dt>備考</dt>
            <dd>
              <textarea v-model="todoStore.formData.memo" placeholder="備考を入力..."></textarea>
            </dd>
          </dl>

          <div class="border"></div>

          <!-- フロー表示部分 - フラットな構造 -->
          <div class="flow" v-if="todoStore.formData && todoStore.formData.todoJson && todoStore.formData.todoJson.length > 0">
            <template v-for="item in flatItems" :key="item.id">
              <div v-if="todoStore.shouldShowItem(item)">
                <details>
                  <summary>
                    <div>
                      <label>
                        <input type="checkbox" :checked="!!item.checkedAt" @change="todoStore.updateItemChecked(item)" />
                        {{ item.label }}
                        <template v-if="item.tip">
                          <span class="material-symbols-outlined" @click="showNote($event, item)">info</span>
                        </template>
                      </label>
                      <div class="tip" v-if="item.note">メモ：{{ item.note }}</div>
                    </div>

                    <!-- チェック情報 -->
                    <div class="check-info-container" v-if="item.checkedAt">
                      <span class="check-info">
                        <span class="date">{{ formatDate(item.checkedAt) }}</span>
                        <br />
                        {{ item.checkedUserName }}
                      </span>
                    </div>
                  </summary>
                  <div class="memo">
                    <textarea v-model="item.note" @change="todoStore.updateItemNoteFromUI(item)" placeholder="メモを入力..."></textarea>
                  </div>
                </details>

                <!-- 選択肢がある場合 -->
                <div class="radios" v-if="item.selection && item.selection.length > 0">
                  <template v-for="(option, optIndex) in item.selection" :key="`option-${item.id}-${optIndex}`">
                    <input type="radio" :id="`option_${item.id}_${optIndex}`" :name="`option_${item.id}`" :checked="todoStore.selectedOptions[item.id] === optIndex" />
                    <label
                      :for="`option_${item.id}_${optIndex}`"
                      @click.prevent="selectOption(item, optIndex)"
                      :class="{
                        checked: todoStore.selectedOptions[item.id] === optIndex,
                      }"
                    >
                      {{ option.option }}
                    </label>
                  </template>
                </div>
              </div>
            </template>

            <!-- フロー状態表示 -->
            <div class="last" v-if="flatItems.length > 0" :class="{ complete: isFlowCompleted }">
              {{ todoStore.formData.todoMasterName }}　
              {{ isFlowCompleted ? '完了' : '未完了' }}
            </div>
          </div>

          <!-- フローがない場合のメッセージ -->
          <div class="flow-placeholder" v-else-if="todoStore.formData && todoStore.formData.todoMasterId">
            <p>種別の取得に失敗しました</p>
          </div>

          <!-- フロー種別が選択されていない場合のメッセージ -->
          <div class="flow-placeholder" v-else>
            <p>種別を選択してください</p>
          </div>
        </div>
      </div>
    </template>
  </EditDialog>
</template>

<style lang="less" scoped>
  .flow-placeholder {
    padding: 20px;
    background-color: #f8f9fa;
    border-radius: 4px;
    text-align: center;
    color: #6c757d;
  }

  .main-header {
    .todo-master {
      background-color: #1a7247; // 緑色に変更（var(--active-color)の代わり）
      color: #fff;
      border: none;
      border-radius: 11px;
      font-size: 12px;
      font-weight: bold;
      width: auto; // 文字列に合わせて自動調整
      min-width: 120px; // 最小幅を設定
      padding: 0 10px; // 左右の余白を追加
      height: 22px;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: opacity 0.3s;
      white-space: nowrap;

      &:hover {
        opacity: 0.7;
      }
    }
  }
</style>
